

export type EuropeCountry =
  "NO" | "IS" | "DK" | "SE" | "FI" // Nordics
  | "IE" | "UK" | "BE" | "NL" | "LU" | "FR" // Western 
  | "DE" | "PL" | "CZ" | "AT" | "CH" // Central
  | "EE" | "LV" | "LT" // Baltics
  // South Eastern
  | "UA" | "MD" | "RO" | "BG" | "MK" | "AL" | "ME" 
  | "BA" // Bosnia and Herzegovina
  | "HR" // Croatia
  | "SI" // Slovenia
  | "AM" // Armenia 
  // South
  | "PT" | "ES" | "IT" | "MT" | "GR" | "TR";

export type MajorAllyCountry =
  "CA" | "MX" | "AU";

export type BoycottCountry = 
  "RU" | "BY" | "SK" | "RS" | "US" | "IL" | "HU";

export type OtherCountry = "OT" | "CN" | "JP" | "FE"; // Other | Federated

export const europeCountries: EuropeCountry[] = [
  "NO", "IS", "DK", "SE", "FI",
  "IE", "UK", "BE", "NL", "LU", "FR",
  "DE", "PL", "CZ", "AT", "CH",
  "EE", "LV", "LT",
  "UA", "MD", "RO", "BG", "MK", "AL", "ME", "BA", "HR", "SI",
  "PT", "ES", "IT", "MT", "GR", "TR"
];

export const isEuropeCountry = (country: string): country is EuropeCountry => {
  return europeCountries.includes(country as EuropeCountry);
};

export const boycottCountries: BoycottCountry[] = ["RU", "BY", "SK", "RS", "US", "IL", "HU"];

export const isBoycottCountry = (country?: string): country is BoycottCountry => {
  if (!country) {
    return false;
  }
  return boycottCountries.includes(country as BoycottCountry);
};

export const majorAllyCountries: MajorAllyCountry[] = ["CA", "MX", "AU"];

export const isMajorAllyCountry = (country: string): country is MajorAllyCountry => {
  return majorAllyCountries.includes(country as MajorAllyCountry);
};

export const isOtherCountry = (country: string): country is OtherCountry => {
  return !isEuropeCountry(country) && !isBoycottCountry(country) && !isMajorAllyCountry(country);
}

export const isFederatedCountry = (country: string) => {
  return country === "FE"
}

export type EuropeOrAllyCountry = EuropeCountry | MajorAllyCountry | "EU";

export type Country = EuropeCountry | BoycottCountry | MajorAllyCountry | OtherCountry | "EU";

export type MainCategory =
  "social-media" | "messaging" | "collaboration" | "streaming" | "services"
  | "travel" | "cars" | "clothing" | "appliances" | "electronics" 
  | "fast-food" | "food-and-snacks" | "drinks" | "personal-care"
  | "household" | "online-shopping" | "hardware" | "heavy-machinery" | "office";

export const mainCategories: MainCategory[] = [
  "social-media", "messaging", "collaboration", "streaming", "services"
 , "travel", "cars", "clothing", "appliances", "electronics" 
 , "fast-food", "food-and-snacks", "drinks", "personal-care"
 , "household", "online-shopping", "hardware", "heavy-machinery", "office"
]; 

export type SubCategory =
  // of social-media
  "social-networking" | "micro-blogging" | "photo-sharing" | "video-sharing" | "discussion"
  // messaging (none)
  // of collaboration
  | "video-meetings" | "collaboration-suite" | "real-time-chat" | "group-forum"
  // of streaming
  | "tv-and-movies" | "music"
  // of services
  | "maps" | "email" | "search-engine" | "office-suite" | "llm"
  // of travel (none)
  // of cars (none)
  // of clothing
  | "athletic" | "clothes" | "shoes" | "jeans"
  // of appliances
  | "major-appliances" | "small-appliances"
  // of electronics
  | "computers" | "phones" | "audio" | "wearables"
  // of fast food
  | "burgers" | "pizza" | "sandwiches" | "other-fast-food"
  // of food-and-snacks
  | "treats" | "crisps" | "cereal" | "crackers" | "ketchup" | "cheese"
  // of drinks
  | "caffeinated" | "energy-drinks" | "soda"
  // of personal-care
  | "hair" | "shaving" | "baby" | "tooth" | "hygiene" | "skin" | "menstruation"
  // of household
  | "laundry" | "cleaning" | "air-freshener" | "batteries" | "dishes"
  // of hardware
  | "gardening" | "power-tools" | "tools"
  // of office
  | "sticky-notes" | "pens" | "markers" | "tape" | "office-tools" | "notebooks" | "whiteboards";

export type Category = MainCategory | SubCategory;

export interface CategoryNode {
  category: Category;
  subcategories?: CategoryNode[];
}

export const categoryTree: CategoryNode[] = [
  {
    category: "social-media",
    subcategories: [
      { category: "social-networking" },
      { category: "micro-blogging" },
      { category: "photo-sharing" },
      { category: "video-sharing" },
      { category: "discussion" },
    ]
  },
  {
    category: "messaging",
  },
  {
    category: "collaboration",
    subcategories: [
      { category: "video-meetings" },
      { category: "collaboration-suite" },
      { category: "real-time-chat" },
      { category: "group-forum" },
    ]
  },
  {
    category: "streaming",
    subcategories: [
      { category: "tv-and-movies" },
      { category: "music" },
    ]
  },
  {
    category: "services",
    subcategories: [
      { category: "maps" },
      { category: "email" },
      { category: "search-engine" },
      { category: "llm" },
      { category: "office-suite" }
    ]
  },
  {
    category: "travel",
  },
  {
    category: "cars",
  },
  {
    category: "clothing",
    subcategories: [
      { category: "athletic" },
      { category: "clothes" },
      { category: "shoes" },
      { category: "jeans" },
    ]
  },
  {
    category: "appliances",
    subcategories: [
      { category: "major-appliances" },
      { category: "small-appliances" },
    ]
  },
  {
    category: "electronics",
    subcategories: [
      { category: "computers" },
      { category: "phones" },
      { category: "audio" },
      { category: "wearables" },
    ]
  },
  {
    category: "fast-food",
    subcategories: [
      { category: "burgers" },
      { category: "pizza" },
      { category: "sandwiches" },
      { category: "other-fast-food" },
    ]
  },
  {
    category: "food-and-snacks",
    subcategories: [
      { category: "treats" },
      { category: "crisps" },
      { category: "crackers" },
      { category: "cereal" },
      { category: "ketchup" },
      { category: "cheese" },
    ]
  },
  {
    category: "drinks",
    subcategories: [
      { category: "caffeinated" },
      { category: "soda" },
      { category: "energy-drinks" },
    ]
  },
  {
    category: "personal-care",
    subcategories: [
      { category: "shaving" },
      { category: "menstruation" },
      { category: "hair" },
      { category: "baby" },
      { category: "tooth" },
      { category: "hygiene" },
      { category: "skin" },
    ]
  },
  {
    category: "household",
    subcategories: [
      { category: "laundry" },
      { category: "cleaning" },
      { category: "air-freshener" },
      { category: "dishes" },
      { category: "batteries" },
    ]
  },
  {
    category: "hardware",
    subcategories: [
      { category: "power-tools" },
      { category: "tools" },
      { category: "gardening" },
    ]
  },
  {
    category: "office",
    subcategories: [
      { category: "sticky-notes" },
      { category: "pens" },
      { category: "markers" },
      { category: "tape" },
      { category: "office-tools" },
      { category: "notebooks" },
      { category: "whiteboards" },
    ]
  },
];


export type BusinessInRussia =
  "continuing" | "pausing" | "scaling-back"
  | "suspension" | "withdrawal" | "exited";

export interface ProductOrBrand {
  id?: string; // For custom products
  name: string;
  iconFile?: string;
  ownerCountry: Country; // Traced ownership
  headquarterIn?: Country;
  availableIn?: EuropeOrAllyCountry[]; // undefined means universally available
  notAvailableIn?: EuropeOrAllyCountry[];
  businessInRussia?: BusinessInRussia;
  isLessEvilAlternative?: boolean; // Non-profit or benefit corporation
  categories: Category[];
  customCategory?: string;
  directAlternativeTo?: string[];
  sourceLinks?: string[];
  include?: boolean;
  description?: string;
  comments?: string;
  userCount?: number;
  revenueEuro?: number;
}